import React, { useState, useEffect, useRef } from 'react';
import { http } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import SignatureCanvas from 'react-signature-canvas';
import {
  Plus, Search, X, Save, Package, Camera,
  Edit, Trash2, Eye, Upload, Image, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const ESTADOS = [
  { value: 'en_laboratorio',                label: 'En laboratorio',   color: 'bg-blue-100 text-blue-800' },
  { value: 'en_reparacion',                 label: 'En reparación',    color: 'bg-yellow-100 text-yellow-800' },
  { value: 'pendiente_presupuesto_repuesto', label: 'Pend. presupuesto',color: 'bg-orange-100 text-orange-800' },
  { value: 'pendiente_entrega',             label: 'Pend. entrega',    color: 'bg-purple-100 text-purple-800' },
  { value: 'entregado',                     label: 'Entregado',        color: 'bg-green-100 text-green-800' },
];
const MODOS_INGRESO = [
  { value: 'recibido_en_taller',   label: 'Recibido en taller' },
  { value: 'retirado_por_tecnico', label: 'Retirado por técnico' },
];
const TIPOS_EQUIPO = ['Desktop','Notebook','Servidor','Impresora','Router','Switch','Otro'];

function Badge({ estado }) {
  const e = ESTADOS.find(x => x.value === estado) || ESTADOS[0];
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${e.color}`}>{e.label}</span>;
}

export default function RecepcionesList() {
  const { user } = useAuth();
  const [recepciones, setRecepciones] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const esAdmin = user?.rol === 'admin';

  useEffect(() => { cargar(); }, []);

  async function cargar() {
    setLoading(true);
    try {
      const [rRes, cRes] = await Promise.all([http.get('/recepciones-equipos'), http.get('/clientes')]);
      setRecepciones(rRes.data || []);
      setClientes(cRes.data || []);
      if (esAdmin) { const uRes = await http.get('/usuarios'); setTecnicos((uRes.data||[]).filter(u=>u.rol==='tecnico')); }
    } catch { toast.error('Error cargando recepciones'); }
    finally { setLoading(false); }
  }

  async function eliminar(id) {
    if (!window.confirm('¿Eliminar esta recepción?')) return;
    try { await http.delete(`/recepciones-equipos/${id}`); toast.success('Eliminada'); cargar(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
  }

  const filtradas = recepciones.filter(r => {
    const q = busqueda.toLowerCase();
    const ok = !q || [r.tipo_equipo,r.marca,r.modelo,r.numero_serie,r.nombre_cliente,r.numero_recepcion].some(v=>(v||'').toLowerCase().includes(q));
    return ok && (!filtroEstado || r.estado === filtroEstado);
  });

  const abrirNueva = () => {
    setEditando(null);
    setShowForm(true);
  };

  return (
    <div className="p-3 sm:p-4 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="w-5 h-5 text-emerald-600" /> Recepción de Equipos
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">Ingreso, estado y entrega de equipos</p>
        </div>
        <button
          onClick={abrirNueva}
          className="flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white px-4 py-3 sm:py-2 rounded-xl text-sm font-medium transition-colors w-full sm:w-auto"
        >
          <Plus className="w-4 h-4" /> Nueva recepción
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            placeholder="Buscar equipo, cliente, N° serie..." value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        </div>
        <select className="border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
          value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
          <option value="">Todos los estados</option>
          {ESTADOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
        </select>
      </div>

      {loading ? <div className="text-center py-12 text-gray-500">Cargando...</div>
      : filtradas.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No hay recepciones registradas</p>
        </div>
      ) : (
        <>
          {/* Móvil: cards */}
          <div className="sm:hidden space-y-2">
            {filtradas.map(r => (
              <div key={r.id||r._id} className="bg-white border rounded-2xl p-4 shadow-sm active:bg-gray-50"
                onClick={() => setDetalle(r)}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{r.tipo_equipo} {r.marca} {r.modelo}</p>
                    <p className="text-xs text-gray-400 font-mono">{r.numero_recepcion}</p>
                  </div>
                  <Badge estado={r.estado} />
                </div>
                <p className="text-xs text-gray-600 mb-0.5">👤 {r.nombre_cliente || '—'}</p>
                {r.numero_serie && <p className="text-xs text-gray-500 mb-0.5">S/N: {r.numero_serie}</p>}
                <p className="text-xs text-gray-500 mb-3">{MODOS_INGRESO.find(m=>m.value===r.modo_ingreso)?.label}</p>
                <div className="flex items-center justify-between" onClick={e=>e.stopPropagation()}>
                  <span className="text-xs text-gray-400">{r.fecha_ingreso ? new Date(r.fecha_ingreso).toLocaleDateString('es-PY') : '—'}</span>
                  <div className="flex gap-2">
                    <button onClick={() => { setEditando(r); setShowForm(true); }}
                      className="flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 px-3 py-2 rounded-xl font-medium active:bg-emerald-100">
                      <Edit className="w-3.5 h-3.5" /> Editar
                    </button>
                    {esAdmin && <button onClick={() => eliminar(r.id||r._id)}
                      className="flex items-center gap-1 text-xs bg-red-50 text-red-600 px-3 py-2 rounded-xl font-medium active:bg-red-100">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>}
                    <ChevronRight className="w-4 h-4 text-gray-300 self-center" />
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Desktop: tabla */}
          <div className="hidden sm:block bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['N° Recepción','Equipo','Cliente','Modo ingreso','Estado','Fecha ingreso','Acciones'].map(h=>(
                      <th key={h} className={`px-4 py-3 font-medium text-gray-500 ${h==='Acciones'?'text-right':'text-left'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtradas.map(r => (
                    <tr key={r.id||r._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">{r.numero_recepcion}</td>
                      <td className="px-4 py-3"><div className="font-medium text-gray-900">{r.tipo_equipo} {r.marca}</div><div className="text-xs text-gray-500">{r.modelo}{r.numero_serie?` · S/N: ${r.numero_serie}`:''}</div></td>
                      <td className="px-4 py-3 text-gray-700">{r.nombre_cliente||r.cliente_id}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{MODOS_INGRESO.find(m=>m.value===r.modo_ingreso)?.label}</td>
                      <td className="px-4 py-3"><Badge estado={r.estado} /></td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{r.fecha_ingreso?new Date(r.fecha_ingreso).toLocaleDateString('es-PY'):'—'}</td>
                      <td className="px-4 py-3"><div className="flex justify-end gap-2">
                        <button onClick={()=>setDetalle(r)} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md"><Eye className="w-4 h-4"/></button>
                        <button onClick={()=>{setEditando(r);setShowForm(true);}} className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-md"><Edit className="w-4 h-4"/></button>
                        {esAdmin&&<button onClick={()=>eliminar(r.id||r._id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md"><Trash2 className="w-4 h-4"/></button>}
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showForm && <RecepcionModal recepcion={editando} clientes={clientes} tecnicos={tecnicos} user={user}
        onClose={()=>{setShowForm(false);setEditando(null);}} onSaved={()=>{setShowForm(false);setEditando(null);cargar();}} />}
      {detalle && <DetalleModal recepcion={detalle} onClose={()=>setDetalle(null)}
        onEdit={()=>{setDetalle(null);setEditando(detalle);setShowForm(true);}} />}
    </div>
  );
}

function RecepcionModal({ recepcion, clientes, tecnicos, user, onClose, onSaved }) {
  const esAdmin  = user?.rol === 'admin';
  const esEdicion = !!recepcion;
  const sigIC = useRef(null), sigIT = useRef(null), sigEC = useRef(null), sigET = useRef(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('datos');
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [fotosIngreso, setFotosIngreso] = useState(recepcion?.fotos_ingreso || []);
  const [fotosEntrega, setFotosEntrega] = useState(recepcion?.fotos_entrega || []);
  const fileIngRef = useRef(null), fileEntRef = useRef(null);
  const uid = user?.id || user?._id || '';

  const [form, setForm] = useState({
    tipo_equipo:    recepcion?.tipo_equipo    || '',
    marca:          recepcion?.marca          || '',
    modelo:         recepcion?.modelo         || '',
    numero_serie:   recepcion?.numero_serie   || '',
    cliente_id:     recepcion?.cliente_id     || '',
    nombre_cliente: recepcion?.nombre_cliente || '',
    tecnico_id:     recepcion?.tecnico_id     || uid,
    nombre_tecnico: recepcion?.nombre_tecnico || (user?.nombre || user?.username || ''),
    modo_ingreso:   recepcion?.modo_ingreso   || 'recibido_en_taller',
    estado:         recepcion?.estado         || 'en_laboratorio',
    fecha_ingreso:  recepcion?.fecha_ingreso  ? new Date(recepcion.fecha_ingreso).toISOString().slice(0,10) : new Date().toISOString().slice(0,10),
    fecha_entrega:  recepcion?.fecha_entrega  ? new Date(recepcion.fecha_entrega).toISOString().slice(0,10) : '',
    detalle:       recepcion?.detalle       || '',
    diagnostico:   recepcion?.diagnostico   || '',
    observaciones: recepcion?.observaciones || '',
    orden_id:      recepcion?.orden_id      || '',
    pendiente_id:  recepcion?.pendiente_id  || '',
    aclaracion_firma_ingreso_cliente: recepcion?.aclaracion_firma_ingreso_cliente || '',
    aclaracion_firma_ingreso_tecnico: recepcion?.aclaracion_firma_ingreso_tecnico || '',
    aclaracion_firma_entrega_cliente: recepcion?.aclaracion_firma_entrega_cliente || '',
    aclaracion_firma_entrega_tecnico: recepcion?.aclaracion_firma_entrega_tecnico || '',
  });

  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const esEntregado = form.estado === 'entregado';

  function firmas() {
    const o={};
    if (sigIC.current&&!sigIC.current.isEmpty()) o.firma_ingreso_cliente=sigIC.current.toDataURL();
    if (sigIT.current&&!sigIT.current.isEmpty()) o.firma_ingreso_tecnico=sigIT.current.toDataURL();
    if (sigEC.current&&!sigEC.current.isEmpty()) o.firma_entrega_cliente=sigEC.current.toDataURL();
    if (sigET.current&&!sigET.current.isEmpty()) o.firma_entrega_tecnico=sigET.current.toDataURL();
    return o;
  }

  async function guardar() {
    if (!form.tipo_equipo)  return toast.error('Tipo de equipo obligatorio');
    if (!form.cliente_id)   return toast.error('Cliente obligatorio');
    if (!form.tecnico_id)   return toast.error('Técnico obligatorio');
    setSaving(true);
    try {
      const payload={
        ...form,
        ...firmas(),
        fecha_ingreso: new Date(form.fecha_ingreso).toISOString(),
        fecha_entrega: esEntregado
          ? (form.fecha_entrega ? new Date(form.fecha_entrega).toISOString() : new Date().toISOString())
          : null,
        firma_entrega_cliente: esEntregado ? undefined : null,
        aclaracion_firma_entrega_cliente: esEntregado ? form.aclaracion_firma_entrega_cliente : '',
        firma_entrega_tecnico: esEntregado ? undefined : null,
        aclaracion_firma_entrega_tecnico: esEntregado ? form.aclaracion_firma_entrega_tecnico : '',
        fotos_ingreso:fotosIngreso,
        fotos_entrega:fotosEntrega,
      };
      if (esEdicion) { await http.put(`/recepciones-equipos/${recepcion.id||recepcion._id}`,payload); toast.success('Actualizada'); }
      else           { await http.post('/recepciones-equipos',payload); toast.success('Creada'); }
      onSaved();
    } catch(e){ toast.error(e.response?.data?.detail||'Error al guardar'); }
    finally { setSaving(false); }
  }

  async function subirFoto(e, momento) {
    const file=e.target.files?.[0]; if(!file) return;
    if (!esEdicion) return toast.error('Guardá la recepción primero para subir fotos');
    setUploadingFoto(true);
    try {
      const fd=new FormData(); fd.append('file',file);
      const res=await http.post(`/recepciones-equipos/${recepcion.id||recepcion._id}/fotos/${momento}`,fd,{headers:{'Content-Type':'multipart/form-data'}});
      if (momento==='ingreso') setFotosIngreso(p=>[...p,res.data]); else setFotosEntrega(p=>[...p,res.data]);
      toast.success('Foto subida');
    } catch(e){ toast.error(e.response?.data?.detail||'Error al subir foto'); }
    finally { setUploadingFoto(false); }
  }

  const TABS=[{key:'datos',label:'Datos'},{key:'firmas_ingreso',label:'Firma ingreso'},{key:'firmas_entrega',label:'Firma entrega'},{key:'fotos',label:'Fotos'}];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-2xl flex flex-col max-h-[92vh] sm:max-h-[85vh]">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b flex-shrink-0 relative">
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 bg-gray-300 rounded-full sm:hidden" />
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 mt-1 sm:mt-0">{esEdicion?'Editar recepción':'Nueva recepción de equipo'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex gap-1 px-4 sm:px-6 pt-3 border-b overflow-x-auto flex-shrink-0 pb-0" style={{scrollbarWidth:'none'}}>
          {TABS.map(t=>{
            const disabled = t.key === 'firmas_entrega' && !esEntregado;
            return (
            <button key={t.key} onClick={()=>!disabled && setActiveTab(t.key)} disabled={disabled}
              className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-t-lg whitespace-nowrap transition-colors ${activeTab===t.key?'bg-emerald-600 text-white':'text-gray-600 hover:bg-gray-100'} ${disabled ? 'opacity-40 cursor-not-allowed hover:bg-transparent' : ''}`}>
              {t.label}
            </button>
          )})}
        </div>

        <div className="overflow-y-auto flex-1 px-4 sm:px-6 py-4">
          {activeTab==='datos' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FSelect label="Tipo de equipo *" value={form.tipo_equipo} onChange={v=>set('tipo_equipo',v)} color="emerald">
                  <option value="">Seleccionar...</option>
                  {TIPOS_EQUIPO.map(t=><option key={t} value={t}>{t}</option>)}
                </FSelect>
                <FSelect label="Modo de ingreso *" value={form.modo_ingreso} onChange={v=>set('modo_ingreso',v)} color="emerald">
                  {MODOS_INGRESO.map(m=><option key={m.value} value={m.value}>{m.label}</option>)}
                </FSelect>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Marca" value={form.marca} onChange={v=>set('marca',v)} />
                <Field label="Modelo" value={form.modelo} onChange={v=>set('modelo',v)} />
                <Field label="N° de serie" value={form.numero_serie} onChange={v=>set('numero_serie',v)} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FSelect label="Cliente *" value={form.cliente_id} onChange={e=>{const c=clientes.find(c=>(c.id||c._id)===e);set('cliente_id',e);set('nombre_cliente',c?.nombre||'');}} color="emerald">
                  <option value="">Seleccionar cliente...</option>
                  {clientes.map(c=><option key={c.id||c._id} value={c.id||c._id}>{c.nombre}</option>)}
                </FSelect>
                {esAdmin
                  ? <FSelect label="Técnico *" value={form.tecnico_id} onChange={e=>{const t=tecnicos.find(t=>(t.id||t._id)===e);set('tecnico_id',e);set('nombre_tecnico',t?.nombre||t?.username||'');}} color="emerald">
                      <option value="">Seleccionar técnico...</option>
                      {tecnicos.map(t=><option key={t.id||t._id} value={t.id||t._id}>{t.nombre||t.username}</option>)}
                    </FSelect>
                  : <Field label="Técnico" value={form.nombre_tecnico} readOnly />
                }
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FSelect label="Estado" value={form.estado} onChange={v=>set('estado',v)} color="emerald">
                  {ESTADOS.map(e=><option key={e.value} value={e.value}>{e.label}</option>)}
                </FSelect>
                <Field label="Fecha ingreso *" type="date" value={form.fecha_ingreso} onChange={v=>set('fecha_ingreso',v)} />
              </div>
              {esEntregado ? (
                <Field label="Fecha de entrega" type="date" value={form.fecha_entrega} onChange={v=>set('fecha_entrega',v)} />
              ) : (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
                  La fecha y las firmas de entrega se cargan recién cuando el equipo se marca como entregado.
                </div>
              )}
              <FTextarea label="Detalle del equipo / síntoma" value={form.detalle} onChange={v=>set('detalle',v)} placeholder="Estado al ingresar..." />
              <FTextarea label="Diagnóstico" value={form.diagnostico} onChange={v=>set('diagnostico',v)} placeholder="Diagnóstico técnico..." />
              <FTextarea label="Observaciones" value={form.observaciones} onChange={v=>set('observaciones',v)} rows={2} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t">
                <Field label="Vínculo Orden (opcional)" value={form.orden_id} onChange={v=>set('orden_id',v)} placeholder="ID de orden..." />
                <Field label="Vínculo Pendiente (opcional)" value={form.pendiente_id} onChange={v=>set('pendiente_id',v)} placeholder="ID de pendiente..." />
              </div>
            </div>
          )}

          {activeTab==='firmas_ingreso' && (
            <div className="space-y-6">
              <FirmaPanel label="Firma del cliente (ingreso)" sigRef={sigIC} aclaracion={form.aclaracion_firma_ingreso_cliente} onAclaracion={v=>set('aclaracion_firma_ingreso_cliente',v)} onClear={()=>sigIC.current?.clear()} existente={recepcion?.firma_ingreso_cliente} />
              <FirmaPanel label="Firma del técnico (ingreso)" sigRef={sigIT} aclaracion={form.aclaracion_firma_ingreso_tecnico} onAclaracion={v=>set('aclaracion_firma_ingreso_tecnico',v)} onClear={()=>sigIT.current?.clear()} existente={recepcion?.firma_ingreso_tecnico} />
            </div>
          )}

          {activeTab==='firmas_entrega' && (
            esEntregado ? (
              <div className="space-y-6">
                <FirmaPanel label="Firma del cliente (entrega)" sigRef={sigEC} aclaracion={form.aclaracion_firma_entrega_cliente} onAclaracion={v=>set('aclaracion_firma_entrega_cliente',v)} onClear={()=>sigEC.current?.clear()} existente={recepcion?.firma_entrega_cliente} />
                <FirmaPanel label="Firma del técnico (entrega)" sigRef={sigET} aclaracion={form.aclaracion_firma_entrega_tecnico} onAclaracion={v=>set('aclaracion_firma_entrega_tecnico',v)} onClear={()=>sigET.current?.clear()} existente={recepcion?.firma_entrega_tecnico} />
              </div>
            ) : (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                Marcá la recepción como entregada para habilitar la fecha y las firmas de entrega.
              </div>
            )
          )}

          {activeTab==='fotos' && (
            <div className="space-y-6">
              <FotoPanel titulo="Fotos de ingreso" fotos={fotosIngreso} onSubir={e=>subirFoto(e,'ingreso')} uploading={uploadingFoto} fileRef={fileIngRef} esEdicion={esEdicion} />
              <FotoPanel titulo="Fotos de entrega" fotos={fotosEntrega} onSubir={e=>subirFoto(e,'entrega')} uploading={uploadingFoto} fileRef={fileEntRef} esEdicion={esEdicion} />
            </div>
          )}
        </div>

        <div className="flex gap-3 px-4 sm:px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-200 rounded-xl">Cancelar</button>
          <button onClick={guardar} disabled={saving}
            className="flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-60 text-white px-5 py-2.5 rounded-xl text-sm font-medium flex-1 sm:flex-none">
            <Save className="w-4 h-4" /> {saving?'Guardando...':esEdicion?'Actualizar':'Crear recepción'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetalleModal({ recepcion: r, onClose, onEdit }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b flex-shrink-0 relative">
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 bg-gray-300 rounded-full sm:hidden" />
          <h2 className="text-base font-semibold text-gray-900 mt-1 sm:mt-0">{r.numero_recepcion}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl"><X className="w-5 h-5" /></button>
        </div>
        <div className="overflow-y-auto flex-1 px-4 sm:px-6 py-4 space-y-3 text-sm">
          <Row label="Equipo"       value={`${r.tipo_equipo||''} ${r.marca||''} ${r.modelo||''}`} />
          <Row label="N° Serie"     value={r.numero_serie} />
          <Row label="Cliente"      value={r.nombre_cliente||r.cliente_id} />
          <Row label="Técnico"      value={r.nombre_tecnico||r.tecnico_id} />
          <Row label="Modo ingreso" value={MODOS_INGRESO.find(m=>m.value===r.modo_ingreso)?.label} />
          <Row label="Estado"       value={<Badge estado={r.estado}/>} />
          <Row label="Fecha ingreso"value={r.fecha_ingreso?new Date(r.fecha_ingreso).toLocaleDateString('es-PY'):'—'} />
          <Row label="Fecha entrega"value={r.fecha_entrega?new Date(r.fecha_entrega).toLocaleDateString('es-PY'):'—'} />
          {r.detalle       && <Row label="Detalle"       value={r.detalle} />}
          {r.diagnostico   && <Row label="Diagnóstico"   value={r.diagnostico} />}
          {r.observaciones && <Row label="Observaciones" value={r.observaciones} />}
          {r.firma_ingreso_cliente && <FirmaImg label="Firma ingreso cliente" src={r.firma_ingreso_cliente} aclaracion={r.aclaracion_firma_ingreso_cliente} />}
          {r.firma_entrega_cliente && <FirmaImg label="Firma entrega cliente" src={r.firma_entrega_cliente} aclaracion={r.aclaracion_firma_entrega_cliente} />}
        </div>
        <div className="flex gap-3 px-4 sm:px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-200 rounded-xl flex-1 sm:flex-none text-center">Cerrar</button>
          <button onClick={onEdit} className="flex items-center justify-center gap-2 bg-emerald-600 text-white px-4 py-2.5 text-sm rounded-xl hover:bg-emerald-700 flex-1 sm:flex-none">
            <Edit className="w-4 h-4" /> Editar
          </button>
        </div>
      </div>
    </div>
  );
}

function FirmaImg({ label, src, aclaracion }) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <img src={src} alt="firma" className="border rounded-lg max-h-24 bg-gray-50 w-full object-contain" />
      {aclaracion && <p className="text-xs text-gray-600 mt-1">{aclaracion}</p>}
    </div>
  );
}

function FirmaPanel({ label, sigRef, aclaracion, onAclaracion, onClear, existente }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      {existente && <div className="mb-2"><p className="text-xs text-gray-500 mb-1">Firma guardada:</p><img src={existente} alt="firma" className="border rounded-lg max-h-20 bg-gray-50"/></div>}
      <p className="text-xs text-gray-500 mb-1">{existente?'Nueva firma (reemplaza la anterior):':'Firmar aquí (dedo o stylus):'}</p>
      <div className="border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-white touch-none">
        <SignatureCanvas ref={sigRef} penColor="#1a1a2e" canvasProps={{className:'w-full',style:{height:160,touchAction:'none'}}} />
      </div>
      <div className="flex gap-2 mt-2">
        <input className="flex-1 border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
          placeholder="Aclaración (nombre completo)" value={aclaracion} onChange={e=>onAclaracion(e.target.value)} />
        <button onClick={onClear} className="text-sm text-white bg-red-500 hover:bg-red-600 active:bg-red-700 px-4 py-2.5 rounded-xl whitespace-nowrap">Borrar</button>
      </div>
    </div>
  );
}

function FotoPanel({ titulo, fotos, onSubir, uploading, fileRef, esEdicion }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-700">{titulo}</span>
        <div>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onSubir} />
          <button onClick={()=>fileRef.current?.click()} disabled={uploading||!esEdicion}
            className="flex items-center gap-1.5 text-sm text-emerald-700 border border-emerald-300 hover:bg-emerald-50 active:bg-emerald-100 px-3 py-2 rounded-xl disabled:opacity-50">
            <Camera className="w-4 h-4" /> {uploading?'Subiendo...':'Agregar foto'}
          </button>
        </div>
      </div>
      {!esEdicion && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-3">⚠️ Guardá la recepción primero para subir fotos.</p>}
      {fotos.length===0
        ? <div className="border-2 border-dashed border-gray-200 rounded-xl py-8 text-center text-gray-400"><Image className="w-8 h-8 mx-auto mb-2 opacity-30"/><p className="text-xs">Sin fotos</p></div>
        : <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">{fotos.map(f=><div key={f.id} className="rounded-xl overflow-hidden border aspect-square bg-gray-100"><img src={f.url} alt={f.filename} className="w-full h-full object-cover"/></div>)}</div>
      }
    </div>
  );
}

function Field({ label, value, onChange, type='text', placeholder, readOnly }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <input type={type} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none disabled:bg-gray-50"
        value={value} onChange={e=>onChange&&onChange(e.target.value)} placeholder={placeholder} readOnly={readOnly} disabled={readOnly} />
    </div>
  );
}

function FSelect({ label, value, onChange, children, color='emerald' }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <select className={`w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-${color}-500 focus:outline-none bg-white`}
        value={value} onChange={e=>onChange(e.target.value)}>{children}</select>
    </div>
  );
}

function FTextarea({ label, value, onChange, placeholder, rows=3 }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <textarea rows={rows} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none resize-none"
        value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="text-xs font-medium text-gray-500 w-28 sm:w-36 shrink-0">{label}</span>
      <span className="text-gray-800 flex-1">{value ?? '—'}</span>
    </div>
  );
}
